import { useEffect, useState } from 'react';
import {
  APP_CONFIG,
  getPlatform,
  isInAppBrowser,
  getStoreUrl,
  buildAndroidIntentUrl,
} from '../utils/deepLink';

const DEEP_LINK = `${APP_CONFIG.scheme}://`;

export default function AppLinkLanding() {
  const platform = getPlatform();
  const storeUrl = getStoreUrl(platform);
  // Only apply in-app browser detection on Android — iOS Universal Links
  // handle the handoff reliably so we let it run its normal flow there.
  const inApp = platform === 'android' && isInAppBrowser();

  // In a real browser we attempt the auto-open and reveal the CTA after a
  // timeout. In a WebView we skip straight to showing the CTA because the
  // auto-open will be silently blocked anyway.
  const [showCta, setShowCta] = useState(inApp);

  useEffect(() => {
    if (inApp || platform === 'desktop') return;

    // Real browser — try to open the app automatically
    window.location.href = DEEP_LINK;

    const timer = setTimeout(() => setShowCta(true), 1800);

    const onHidden = () => { if (document.hidden) clearTimeout(timer); };
    document.addEventListener('visibilitychange', onHidden);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [inApp, platform]);

  function handleOpenApp() {
    if (platform === 'android') {
      window.location.href = buildAndroidIntentUrl(DEEP_LINK);
    } else {
      window.location.href = DEEP_LINK;
    }
  }

  function handleGetApp() {
    window.location.href = storeUrl;
  }

  // Desktop — just show store links
  if (platform === 'desktop') {
    return (
      <Page>
        <Logo />
        <h1 style={s.appName}>{APP_CONFIG.name}</h1>
        <p style={s.tagline}>The Daily Riddle</p>
        <p style={s.body}>Download the app on your phone.</p>
        <StoreBadges />
      </Page>
    );
  }

  // Mobile — waiting to auto-open
  if (!showCta) {
    return (
      <Page>
        <Logo />
        <h1 style={s.appName}>{APP_CONFIG.name}</h1>
        <p style={s.tagline}>The Daily Riddle</p>
        <div style={s.spinner} />
        <p style={s.body}>Opening the app…</p>
      </Page>
    );
  }

  // Mobile — CTA (shown immediately in WebView, or after timeout in real browser)
  return (
    <Page>
      <Logo />
      <h1 style={s.appName}>{APP_CONFIG.name}</h1>
      <p style={s.tagline}>The Daily Riddle</p>

      <div style={s.ctaBox}>
        <p style={s.ctaHeadline}>Open in the {APP_CONFIG.name} app</p>
        <p style={s.ctaBody}>You'll get the best experience in the app.</p>

        <button onClick={handleOpenApp} style={s.btnPrimary}>
          Open app
        </button>
      </div>

      <div style={s.getAppBox}>
        <p style={s.getAppLabel}>Don't have the app?</p>
        {platform === 'ios' ? (
          <button onClick={handleGetApp} style={s.btnSecondary}>
            Download on the App Store
          </button>
        ) : (
          <button onClick={handleGetApp} style={s.btnGooglePlay}>
            <GooglePlayIcon />
            <div style={s.googlePlayText}>
              <small style={s.googlePlaySmall}>GET IT ON</small>
              <span style={s.googlePlayTitle}>Google Play</span>
            </div>
          </button>
        )}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Small shared sub-components
// ---------------------------------------------------------------------------

function Page({ children }) {
  return (
    <div style={s.page}>
      <div style={s.card}>{children}</div>
    </div>
  );
}

function Logo() {
  return <img src="/assets-in-site/icon.png" alt="RiddleMe" style={s.logo} />;
}

// Official Google Play icon — the 4-colour play button triangle
function GooglePlayIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.22 0C.88.2.667.582.667 1.03v21.94c0 .448.213.83.553 1.03l.116.064 12.29-12.29v-.29L1.336-.064z" fill="#4285F4"/>
      <path d="M17.46 16.78l-4.095-4.095v-.29l4.097-4.097.093.053 4.854 2.758c1.386.787 1.386 2.075 0 2.862l-4.854 2.758-.094.052z" fill="#FBBC05"/>
      <path d="M17.555 16.727L13.367 12.54 1.22 24.686c.457.484 1.212.544 1.848.125l14.487-8.084z" fill="#EA4335"/>
      <path d="M17.555 7.273L3.068-.288C2.432-.706 1.677-.646 1.22.125L13.367 11.46l4.188-4.188z" fill="#34A853"/>
    </svg>
  );
}

function StoreBadges() {
  return (
    <div style={s.badges}>
      <a href={APP_CONFIG.iosStoreUrl} style={{ ...s.badge, background: '#000', border: '1px solid #333' }}>
        <svg style={s.badgeIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        <div>
          <small style={s.badgeSmall}>Download on the</small>
          <div>App Store</div>
        </div>
      </a>
      <a href={APP_CONFIG.androidStoreUrl} style={{ ...s.badge, background: '#01875f' }}>
        <svg style={s.badgeIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.18 23.49c-.35-.55-.18-1.19-.18-1.82V2.34C3 1.7 2.83 1.06 3.18.5l9.6 11.5-9.6 11.49zM20.35 11l-2.72-1.57L14 12l3.63 2.57L20.35 13c.95-.55.95-1.45 0-2zM4.34 0l10.4 6-2.28 2.72L4.34 0zM4.34 24l8.12-8.72L14.74 18 4.34 24z" />
        </svg>
        <div>
          <small style={s.badgeSmall}>Get it on</small>
          <div>Google Play</div>
        </div>
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    color: '#fff',
  },
  card: { maxWidth: 400, width: '100%', textAlign: 'center' },
  logo: { width: 72, height: 72, borderRadius: 16, marginBottom: 12 },
  appName: { fontSize: 26, fontWeight: 700, margin: '0 0 4px' },
  tagline: { fontSize: 14, color: '#a0aec0', margin: '0 0 20px' },
  body: { fontSize: 14, color: '#a0aec0', margin: '8px 0 0' },
  spinner: {
    width: 32, height: 32,
    border: '3px solid rgba(255,255,255,0.15)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    margin: '0 auto 12px',
    animation: 'spin 0.8s linear infinite',
  },

  ctaBox: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 16,
    padding: '16px 20px',
    marginBottom: 12,
  },
  ctaHeadline: { fontSize: 18, fontWeight: 700, margin: '0 0 6px' },
  ctaBody: { fontSize: 13, color: '#a0aec0', margin: '0 0 20px', lineHeight: 1.5 },

  btnPrimary: {
    width: '100%', padding: '14px',
    background: '#4f46e5', color: '#fff',
    border: 'none', borderRadius: 12,
    fontSize: 16, fontWeight: 700, cursor: 'pointer',
  },

  getAppBox: { textAlign: 'center' },
  getAppLabel: { fontSize: 13, color: '#a0aec0', margin: '0 0 12px' },
  btnSecondary: {
    width: '100%', padding: '14px',
    background: 'rgba(255,255,255,0.08)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)', borderRadius: 12,
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  btnGooglePlay: {
    width: '100%', padding: '12px 20px',
    background: '#000', color: '#fff',
    border: '1px solid #333', borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 12, cursor: 'pointer',
  },
  googlePlayText: { textAlign: 'left', lineHeight: 1.2 },
  googlePlaySmall: { fontSize: 10, letterSpacing: '0.05em', opacity: 0.8, display: 'block', fontWeight: 400 },
  googlePlayTitle: { fontSize: 18, fontWeight: 600, display: 'block', letterSpacing: '0.01em' },

  badges: { display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', marginTop: 16 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    padding: '13px 24px', borderRadius: 12,
    textDecoration: 'none', color: '#fff',
    fontSize: 15, fontWeight: 600, width: 230, justifyContent: 'center',
  },
  badgeIcon: { width: 22, height: 22, flexShrink: 0 },
  badgeSmall: { fontSize: 10, opacity: 0.8, display: 'block', fontWeight: 400 },
};
